(() => {
  if (customElements.get('yumi-footer-clouds')) return;

  const symbols = '.:+*0123456789X#@';
  const smooth = (low, high, value) => {
    const n = Math.max(0, Math.min(1, (value - low) / (high - low)));
    return n * n * (3 - 2 * n);
  };

  class YumiFooterClouds extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.footer = this.closest('footer');
      this.canvas = this.querySelector('canvas');
      if (!this.footer || !this.canvas) return;
      this.controller = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.connection = navigator.connection;
      this.time = 0;
      this.last = 0;
      this.visible = false;
      this.dirty = true;
      this.ready = false;
      this.frame = 0;
      this.timer = 0;
      this.idle = 0;
      this.width = 0;
      this.height = 0;
      this.ratio = 1;
      this.metrics = { frames: 0, totalMs: 0, maxMs: 0, lastMs: 0, cells: 0, changedCells: 0, state: 'idle' };
      this.readSettings();
      if (window.Shopify?.designMode) {
        this.settingsObserver = new MutationObserver(() => { this.readSettings(); this.sync(); });
        this.observeSettings();
      }
      const options = { signal: this.controller.signal };
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.motion.addEventListener('change', () => this.sync(), options);
      this.connection?.addEventListener('change', () => this.sync(), options);
      const invalidate = event => {
        if (event?.type?.startsWith('shopify:') && !this.footer.contains(event.target)) return;
        this.readSettings();
        this.observeSettings();
        this.dirty = true;
        this.sync();
      };
      document.addEventListener('shopify:section:load', invalidate, options);
      document.addEventListener('shopify:section:reorder', invalidate, options);
      document.addEventListener('shopify:section:unload', invalidate, options);
      this.resizeObserver = new ResizeObserver(invalidate);
      this.resizeObserver.observe(this.footer);
      this.bar = this.footer.querySelector('.yumi-footer__bar');
      if (this.bar) this.resizeObserver.observe(this.bar);
      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          this.sync();
        });
        this.intersectionObserver.observe(this.footer);
      } else {
        this.visible = true;
        this.sync();
      }
    }

    disconnectedCallback() {
      this.stop();
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.settingsObserver?.disconnect();
      this.controller = null;
      this.canvas.width = 1;
      this.canvas.height = 1;
      if (this.atlas) { this.atlas.width = 1; this.atlas.height = 1; }
      this.grid = null;
      this.columns = [];
      this.rows = [];
    }

    readSettings() {
      this.settingsSource = this.footer.querySelector('[data-yumi-cloud-settings]');
      const color = this.settingsSource?.dataset.cloudColor || this.dataset.color || '#ffe5eb';
      const speed = Number(this.settingsSource?.dataset.cloudSpeed ?? this.dataset.speed ?? 2);
      const nextColor = /^#[\da-f]{6}$/i.test(color) ? color : '#ffe5eb';
      if (this.color !== nextColor) this.dirty = true;
      this.color = nextColor;
      this.speed = Number.isFinite(speed) ? Math.max(0, Math.min(5, speed)) : 2;
    }

    observeSettings() {
      this.settingsObserver?.disconnect();
      if (this.settingsSource) this.settingsObserver?.observe(this.settingsSource, {
        attributes: true, attributeFilter: ['data-cloud-color', 'data-cloud-speed']
      });
    }

    stop() {
      clearTimeout(this.timer);
      cancelAnimationFrame(this.frame);
      if (this.idle) {
        if ('cancelIdleCallback' in window) cancelIdleCallback(this.idle);
        else clearTimeout(this.idle);
      }
      this.timer = this.frame = this.idle = 0;
      this.last = 0;
    }

    sync() {
      if (!this.isConnected) return;
      if (!this.visible || document.hidden) {
        this.stop();
        this.metrics.state = 'paused';
        return;
      }
      this.static = this.speed === 0 || this.motion.matches || Boolean(this.connection?.saveData);
      if (this.static && this.ready && !this.dirty) {
        this.stop();
        this.metrics.state = 'static';
        return;
      }
      if (this.idle || this.frame || this.timer) return;
      if (!this.ready) {
        const initialize = () => {
          this.idle = 0;
          if (!this.isConnected || !this.visible || document.hidden) return;
          this.initialize();
          if (this.ready) this.tick(performance.now());
        };
        this.idle = 'requestIdleCallback' in window
          ? requestIdleCallback(initialize, { timeout: 150 })
          : setTimeout(initialize, 16);
      } else {
        this.frame = requestAnimationFrame(now => this.tick(now));
      }
    }

    initialize() {
      this.ctx = this.canvas.getContext('2d', { alpha: false });
      if (!this.ctx) return;
      this.grid = new Float32Array(65536);
      let seed = 417;
      for (let i = 0; i < this.grid.length; i++) {
        seed = (Math.imul(seed, 1664525) + 1013904223) | 0;
        this.grid[i] = (seed >>> 0) / 4294967295;
      }
      this.atlas = document.createElement('canvas');
      this.ready = true;
      this.dirty = true;
    }

    noise(x, y) {
      const ix = Math.floor(x), iy = Math.floor(y);
      let fx = x - ix, fy = y - iy;
      fx = fx * fx * (3 - 2 * fx);
      fy = fy * fy * (3 - 2 * fy);
      const a = this.grid[((iy & 255) << 8) | (ix & 255)];
      const b = this.grid[((iy & 255) << 8) | ((ix + 1) & 255)];
      const c = this.grid[(((iy + 1) & 255) << 8) | (ix & 255)];
      const d = this.grid[(((iy + 1) & 255) << 8) | ((ix + 1) & 255)];
      return a + (b - a) * fx + (c - a) * fy + (a - b - c + d) * fx * fy;
    }

    texture(x, y) {
      return this.noise(x, y) * .59 + this.noise(x * 2.08 + 19, y * 2.08 + 7) * .28
        + this.noise(x * 4.3 + 31, y * 4.3 + 41) * .13;
    }

    resize() {
      const bounds = this.footer.getBoundingClientRect();
      this.width = Math.ceil(bounds.width);
      this.height = Math.ceil(bounds.height);
      if (!this.width || !this.height) return false;
      const mobile = this.width < 750;
      this.interval = 1000 / (mobile ? 12 : 18);
      this.average = 0;
      this.ratio = Math.min(devicePixelRatio || 1, 1.5, Math.sqrt(1400000 / (this.width * this.height)));
      this.canvas.width = Math.ceil(this.width * this.ratio);
      this.canvas.height = Math.ceil(this.height * this.ratio);
      const maxCells = mobile ? 6000 : 12000;
      const size = Math.max(9, Math.sqrt(this.width * this.height / (maxCells * .61 * .84)));
      this.tileW = Math.max(1, Math.round(size * .61 * this.ratio));
      this.tileH = Math.max(1, Math.round(size * .84 * this.ratio));
      const stepX = this.tileW / this.ratio, stepY = this.tileH / this.ratio;
      this.columns = [];
      this.rows = [];
      for (let c = 0; c * stepX < this.width; c++) {
        const x = c * stepX;
        this.columns.push({ x, u: x / Math.max(this.width, 560), c });
      }
      for (let r = 0; r * stepY < this.height; r++) {
        const y = r * stepY;
        this.rows.push({ y, v: y / this.height, r });
      }
      this.metrics.cells = this.columns.length * this.rows.length;
      this.previousCells = new Uint8Array(this.metrics.cells);
      this.ctx.setTransform(1, 0, 0, 1, 0, 0);
      this.ctx.globalAlpha = 1;
      this.ctx.fillStyle = '#ffffff';
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
      this.atlas.width = this.tileW * (1 + symbols.length * 4);
      this.atlas.height = this.tileH;
      const atlasContext = this.atlas.getContext('2d');
      atlasContext.fillStyle = '#ffffff';
      atlasContext.fillRect(0, 0, this.atlas.width, this.atlas.height);
      atlasContext.fillStyle = this.color;
      atlasContext.font = `700 ${size * this.ratio}px "Courier New", monospace`;
      atlasContext.textBaseline = 'middle';
      for (let shade = 0; shade < 4; shade++) {
        atlasContext.globalAlpha = .70 + shade * .10;
        for (let i = 0; i < symbols.length; i++) {
          atlasContext.fillText(symbols[i], (1 + shade * symbols.length + i) * this.tileW, this.tileH * .52);
        }
      }
      this.bar = this.footer.querySelector('.yumi-footer__bar');
      const bar = this.bar?.getBoundingClientRect();
      this.opening = bar ? {
        x: bar.left - bounds.left + bar.width * .51,
        y: bar.top - bounds.top + bar.height * .5,
        rx: Math.max(1, bar.width * .5), ry: bar.height * .5 + 15
      } : null;
      this.dirty = false;
      return true;
    }

    draw() {
      const ctx = this.ctx, time = this.time, opening = this.opening;
      let cellIndex = 0, changedCells = 0;
      for (const col of this.columns) {
        const u = col.u + time * .0035;
        const upperCenter = .30 + .055 * Math.sin(u * 5.4 + 1.5);
        const lowerBase = .70 + .085 * Math.sin(u * 5.2 + 2.1);
        const pocket = Math.sin(u * 9 + 1.9) * .028;
        const openingX = opening ? (col.x - opening.x) / opening.rx : 0;
        const openingWave = Math.sin(u * 12 + 1.4) * 4;
        for (const row of this.rows) {
          const v = row.v;
          const billow = this.texture(u * 4.9 + 2, v * 6.1 + 11 + time * .0018);
          const detail = this.noise(u * 22 + 23, v * 22 + time * .006);
          const upper = .135 - Math.abs(v + (billow - .5) * .22 - upperCenter);
          const lower = v - lowerBase - (billow - .5) * .30;
          let cloud = smooth(-.045, .055, Math.max(upper + pocket, lower) + (detail - .5) * .026);
          if (opening && cloud >= .025) {
            const openingY = (row.y - opening.y - openingWave - (detail - .5) * 5) / opening.ry;
            cloud *= smooth(.72, 1.08, openingX * openingX + openingY * openingY);
          }
          let code = 0;
          if (cloud >= .025) {
            const hash = this.grid[((row.r & 255) << 8) | (col.c & 255)];
            const mass = cloud * (.60 + .40 * billow);
            if (hash <= smooth(0, .66, mass)) {
              const breathing = Math.sin(time * .20 + col.u * 9 + v * 13) * .6;
              const index = Math.max(0, Math.min(symbols.length - 1, Math.floor(2 + mass * 13 + breathing + (hash - .5) * 4)));
              code = 1 + Math.round(cloud * 3) * symbols.length + index;
            }
          }
          if (this.previousCells[cellIndex] !== code) {
            ctx.drawImage(this.atlas, code * this.tileW, 0, this.tileW, this.tileH, col.c * this.tileW, row.r * this.tileH, this.tileW, this.tileH);
            this.previousCells[cellIndex] = code;
            changedCells++;
          }
          cellIndex++;
        }
      }
      this.metrics.changedCells = changedCells;
    }

    tick(now) {
      this.frame = 0;
      if (!this.isConnected || !this.visible || document.hidden) { this.stop(); return; }
      const start = performance.now();
      if (this.dirty && !this.resize()) { this.stop(); return; }
      this.static = this.speed === 0 || this.motion.matches || Boolean(this.connection?.saveData);
      if (this.last && !this.static) this.time += Math.min((now - this.last) / 1000, .25) * this.speed;
      this.last = now;
      this.draw();
      const cost = performance.now() - start;
      this.metrics.frames++;
      this.metrics.totalMs += cost;
      this.metrics.lastMs = cost;
      this.metrics.maxMs = Math.max(this.metrics.maxMs, cost);
      this.average = this.average ? this.average * .9 + cost * .1 : cost;
      if (this.average > 8 && this.interval < 125) this.interval = Math.min(125, this.interval * 1.15);
      this.metrics.state = this.static ? 'static' : 'animating';
      if (this.static) { this.last = 0; return; }
      this.timer = setTimeout(() => {
        this.timer = 0;
        if (this.isConnected && this.visible && !document.hidden) {
          this.frame = requestAnimationFrame(next => this.tick(next));
        }
      }, Math.max(0, this.interval - cost));
    }
  }

  customElements.define('yumi-footer-clouds', YumiFooterClouds);
})();
